/**
 * Mega Menu Components
 *
 * A set of reusable, composable components for building mega-menu navigation.
 * These components are headless/unstyled primitives that handle the interaction
 * logic (dropdowns, mobile menus, keyboard navigation) while allowing apps
 * to configure their own navigation items and routes.
 *
 * @example
 * ```gts
 * import { MegaMenu } from 'tio-ui/components/mega-menu';
 *
 * const NAV_ITEMS = [
 *   { id: 'home', icon: PhHouse, label: 'Home', route: 'authenticated.home' },
 *   { id: 'settings', icon: PhGear, label: 'Settings', dropdown: [...] },
 * ];
 *
 * <MegaMenu @items={{NAV_ITEMS}} @isFullscreen={{false}} />
 * ```
 */

import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { LinkTo } from '@ember/routing';
import { eq } from 'tio-ui/utilities';
import { PhCaretDown, PhCaretRight, PhList, PhX } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';

/**
 * Modifier that positions a flyout panel based on available viewport space.
 * If there's not enough room on the right, it positions to the left.
 */
const positionFlyout = modifier((element: HTMLElement) => {
  const parent = element.parentElement;
  if (!parent) return;

  const parentRect = parent.getBoundingClientRect();
  const flyoutWidth = element.offsetWidth;
  const viewportWidth = window.innerWidth;

  // Check if flyout would overflow on the right
  const spaceOnRight = viewportWidth - parentRect.right;
  const spaceOnLeft = parentRect.left;

  if (spaceOnRight < flyoutWidth && spaceOnLeft > spaceOnRight) {
    // Position to the left
    element.style.left = 'auto';
    element.style.right = '100%';
    element.style.marginLeft = '0';
    element.style.marginRight = '-0.5rem'; // -ml-2 equivalent
  } else {
    // Position to the right (default)
    element.style.left = '100%';
    element.style.right = 'auto';
    element.style.marginLeft = '-0.5rem';
    element.style.marginRight = '0';
  }
});

/**
 * Configuration for a flyout submenu item (leaf node)
 */
export interface MegaMenuFlyoutItem {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** Ember route to navigate to */
  route: string;
}

/**
 * Configuration for a dropdown menu item with a direct route
 */
export interface MegaMenuDropdownItemWithRoute {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** Ember route to navigate to */
  route: string;
  /** Optional stat/badge to display (e.g., "$125/mo", "84/120") */
  stat?: string;
  /** No flyout for route items */
  flyout?: never;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

/**
 * Configuration for a dropdown menu item with a flyout submenu
 */
export interface MegaMenuDropdownItemWithFlyout {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** No direct route for flyout items */
  route?: never;
  /** Optional stat/badge to display */
  stat?: string;
  /** Nested flyout submenu */
  flyout: MegaMenuFlyoutItem[];
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

/**
 * Configuration for a dropdown menu item (either route or flyout)
 */
export type MegaMenuDropdownItem = MegaMenuDropdownItemWithRoute | MegaMenuDropdownItemWithFlyout;

/**
 * Navigation item with a direct route (no dropdown)
 */
export interface MegaMenuItemWithRoute {
  /** Unique identifier for the nav item */
  id: string;
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Ember route to navigate to */
  route: string;
  /** Never has a dropdown */
  dropdown?: never;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

/**
 * Navigation item with a dropdown menu
 */
export interface MegaMenuItemWithDropdown {
  /** Unique identifier for the nav item */
  id: string;
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Never has a direct route */
  route?: never;
  /** Dropdown menu items */
  dropdown: MegaMenuDropdownItem[];
  /** Optional route for the "View Dashboard" CTA (overrides auto-detection) */
  indexRoute?: string;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

export type MegaMenuItem = MegaMenuItemWithRoute | MegaMenuItemWithDropdown;

export interface MegaMenuSignature {
  Args: {
    /** Navigation items configuration */
    items: MegaMenuItem[];
    /** Whether layout is fullscreen (affects container width) */
    isFullscreen?: boolean;
    /** Optional logo/brand component to render */
    logo?: ComponentLike<{ Element: HTMLElement }>;
    /** Label for the mobile brand display */
    brandLabel?: string;
  };
  Element: HTMLElement;
}

/**
 * Helper to get the first dropdown route - used for "View Dashboard" CTA
 */
const getFirstDropdownRoute = (item: MegaMenuItem): string => {
  // Use explicit index route if provided
  if ('indexRoute' in item && item.indexRoute) {
    return item.indexRoute;
  }
  if (item.dropdown && item.dropdown.length > 0) {
    // Find the first item that has a direct route (not a flyout)
    const firstWithRoute = item.dropdown.find((d) => d.route);
    if (firstWithRoute?.route) {
      return firstWithRoute.route;
    }
    // If all have flyouts, get the first flyout item's route
    const firstWithFlyout = item.dropdown.find((d) => d.flyout && d.flyout.length > 0);
    if (firstWithFlyout?.flyout?.[0]?.route) {
      return firstWithFlyout.flyout[0].route;
    }
  }
  return '';
};

/**
 * Helper to filter visible dropdown items
 */
const getVisibleDropdownItems = (items: MegaMenuDropdownItem[]): MegaMenuDropdownItem[] => {
  return items.filter((item) => item.isVisible !== false);
};

/**
 * MegaMenu - Full mega-menu navigation component
 *
 * Features:
 * - Desktop: Horizontal nav with mega-menu dropdowns
 * - Mobile: Hamburger menu with slide-out panel and accordions
 * - Full keyboard navigation (arrow keys, Home/End, Escape)
 * - Dark mode support
 */
export class MegaMenu extends Component<MegaMenuSignature> {
  @tracked openDropdownId: string | null = null;
  @tracked isMobileMenuOpen = false;
  @tracked mobileExpandedId: string | null = null;
  @tracked mobileExpandedFlyout: string | null = null;
  @tracked openFlyoutLabel: string | null = null;
  @tracked focusedMenuIndex = -1;
  @tracked focusedFlyoutIndex = -1;

  @cached
  get visibleNavItems() {
    return this.args.items.filter((item) => item.isVisible !== false);
  }

  get brandLabel() {
    return this.args.brandLabel ?? 'TIO';
  }

  toggleDropdown = (id: string, event?: MouseEvent) => {
    if (this.openDropdownId === id) {
      this.openDropdownId = null;
    } else {
      this.openDropdownId = id;
    }
    // Safari doesn't focus <button> on click. Re-focus the trigger after
    // Glimmer re-renders so keyboard events (ArrowDown, Escape) fire on it.
    if (event) {
      const btn = event.currentTarget as HTMLElement;
      requestAnimationFrame(() => btn?.focus());
    }
  };

  closeDropdown = () => {
    this.openDropdownId = null;
  };

  // Deferred close for links - prevents interference with LinkTo navigation
  handleLinkClick = () => {
    setTimeout(() => {
      this.openDropdownId = null;
      this.isMobileMenuOpen = false;
      this.mobileExpandedId = null;
      this.mobileExpandedFlyout = null;
      this.openFlyoutLabel = null;
    }, 0);
  };

  // Flyout hover handlers - container-based, no debounce needed
  openFlyout = (label: string) => {
    this.openFlyoutLabel = label;
  };

  closeFlyout = () => {
    this.openFlyoutLabel = null;
  };

  toggleMobileMenu = () => {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (!this.isMobileMenuOpen) {
      this.mobileExpandedId = null;
      this.mobileExpandedFlyout = null;
    }
  };

  closeMobileMenu = () => {
    this.isMobileMenuOpen = false;
    this.mobileExpandedId = null;
    this.mobileExpandedFlyout = null;
  };

  toggleMobileExpanded = (id: string) => {
    if (this.mobileExpandedId === id) {
      this.mobileExpandedId = null;
    } else {
      this.mobileExpandedId = id;
    }
    this.mobileExpandedFlyout = null;
  };

  toggleMobileFlyout = (label: string) => {
    if (this.mobileExpandedFlyout === label) {
      this.mobileExpandedFlyout = null;
    } else {
      this.mobileExpandedFlyout = label;
    }
  };

  // Get the current open dropdown's visible items
  get currentDropdownItems() {
    const openItem = this.visibleNavItems.find((item) => item.id === this.openDropdownId);
    const items = openItem?.dropdown ?? [];
    return getVisibleDropdownItems(items);
  }

  // Get the current open flyout's items
  get currentFlyoutItems() {
    if (!this.openFlyoutLabel) return [];
    const flyoutItem = this.currentDropdownItems.find(
      (item) => item.label === this.openFlyoutLabel && item.flyout
    );
    return flyoutItem?.flyout ?? [];
  }

  // Focus a menu item by index using DOM query
  focusMenuItem = (index: number) => {
    const itemCount = this.currentDropdownItems.length;
    if (index >= 0 && index < itemCount) {
      this.focusedMenuIndex = index;
      this.focusedFlyoutIndex = -1;
      // Query for the menu items in the DOM
      requestAnimationFrame(() => {
        const menuItems = document.querySelectorAll(
          '[role="menu"] > div > [role="menuitem"], [role="menu"] > div > div > [role="menuitem"]'
        );
        const item = menuItems[index] as HTMLElement;
        item?.focus();
      });
    }
  };

  // Focus a flyout item by index using DOM query
  focusFlyoutItem = (index: number) => {
    const flyoutCount = this.currentFlyoutItems.length;
    if (index >= 0 && index < flyoutCount) {
      this.focusedFlyoutIndex = index;
      // Query for flyout menu items specifically (in the nested role="menu")
      requestAnimationFrame(() => {
        const flyoutMenus = document.querySelectorAll('[role="menu"] [role="menu"]');
        const flyoutMenu = flyoutMenus[flyoutMenus.length - 1]; // Get the last (most nested) menu
        if (flyoutMenu) {
          const flyoutItems = flyoutMenu.querySelectorAll('[role="menuitem"]');
          const item = flyoutItems[index] as HTMLElement;
          item?.focus();
        }
      });
    }
  };

  // Handle keyboard navigation on dropdown trigger
  handleTriggerKeyDown = (id: string, event: KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        this.closeDropdown();
        // Focus the trigger button
        {
          const trigger = (event.target as HTMLElement).closest('button');
          trigger?.focus();
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggleDropdown(id);
        // Focus first menu item when opening
        if (this.openDropdownId === id) {
          this.focusedMenuIndex = -1;
          this.focusedFlyoutIndex = -1;
          // Wait for render then focus first item
          requestAnimationFrame(() => {
            this.focusMenuItem(0);
          });
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.openDropdownId !== id) {
          this.toggleDropdown(id);
          requestAnimationFrame(() => {
            this.focusMenuItem(0);
          });
        } else {
          this.focusMenuItem(0);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.openDropdownId !== id) {
          this.toggleDropdown(id);
          requestAnimationFrame(() => {
            const lastIndex = this.currentDropdownItems.length - 1;
            this.focusMenuItem(lastIndex);
          });
        } else {
          const lastIndex = this.currentDropdownItems.length - 1;
          this.focusMenuItem(lastIndex);
        }
        break;
    }
  };

  // Handle keyboard navigation within dropdown menu
  handleMenuKeyDown = (
    index: number,
    hasFlyout: boolean,
    flyoutLabel: string,
    event: KeyboardEvent
  ) => {
    const itemCount = this.currentDropdownItems.length;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.closeFlyout();
        this.closeDropdown();
        // Focus the trigger button
        const trigger = document.querySelector('button[aria-expanded="true"]') as HTMLElement;
        trigger?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.focusedFlyoutIndex >= 0 && this.openFlyoutLabel) {
          // Navigate within flyout
          const flyoutCount = this.currentFlyoutItems.length;
          const nextFlyoutIndex = (this.focusedFlyoutIndex + 1) % flyoutCount;
          this.focusFlyoutItem(nextFlyoutIndex);
        } else {
          // Navigate to next menu item
          const nextIndex = (index + 1) % itemCount;
          this.closeFlyout();
          this.focusMenuItem(nextIndex);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.focusedFlyoutIndex >= 0 && this.openFlyoutLabel) {
          // Navigate within flyout
          const flyoutCount = this.currentFlyoutItems.length;
          const prevFlyoutIndex =
            this.focusedFlyoutIndex <= 0 ? flyoutCount - 1 : this.focusedFlyoutIndex - 1;
          this.focusFlyoutItem(prevFlyoutIndex);
        } else {
          // Navigate to previous menu item
          const prevIndex = index <= 0 ? itemCount - 1 : index - 1;
          this.closeFlyout();
          this.focusMenuItem(prevIndex);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (hasFlyout) {
          // Open flyout and focus first item
          this.openFlyout(flyoutLabel);
          requestAnimationFrame(() => {
            this.focusFlyoutItem(0);
          });
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (this.openFlyoutLabel) {
          // Close flyout and return to menu item
          this.closeFlyout();
          this.focusedFlyoutIndex = -1;
          this.focusMenuItem(index);
        }
        break;
      case 'Home':
        event.preventDefault();
        if (this.focusedFlyoutIndex >= 0 && this.openFlyoutLabel) {
          this.focusFlyoutItem(0);
        } else {
          this.focusMenuItem(0);
        }
        break;
      case 'End':
        event.preventDefault();
        if (this.focusedFlyoutIndex >= 0 && this.openFlyoutLabel) {
          const flyoutCount = this.currentFlyoutItems.length;
          this.focusFlyoutItem(flyoutCount - 1);
        } else {
          this.focusMenuItem(itemCount - 1);
        }
        break;
      case 'Tab':
        // Allow tab to close menu and move to next element
        this.closeFlyout();
        this.closeDropdown();
        break;
      case 'Enter':
      case ' ':
        if (hasFlyout && this.focusedFlyoutIndex < 0) {
          // Open flyout when on a flyout trigger
          event.preventDefault();
          this.openFlyout(flyoutLabel);
          requestAnimationFrame(() => {
            this.focusFlyoutItem(0);
          });
        }
        // Otherwise let the default behavior (link navigation) happen
        break;
    }
  };

  // Handle keyboard navigation within flyout
  handleFlyoutKeyDown = (menuIndex: number, flyoutIndex: number, event: KeyboardEvent) => {
    const flyoutCount = this.currentFlyoutItems.length;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.closeFlyout();
        this.focusedFlyoutIndex = -1;
        this.focusMenuItem(menuIndex);
        break;
      case 'ArrowDown':
        event.preventDefault();
        {
          const nextIndex = (flyoutIndex + 1) % flyoutCount;
          this.focusFlyoutItem(nextIndex);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        {
          const prevIndex = flyoutIndex <= 0 ? flyoutCount - 1 : flyoutIndex - 1;
          this.focusFlyoutItem(prevIndex);
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.closeFlyout();
        this.focusedFlyoutIndex = -1;
        this.focusMenuItem(menuIndex);
        break;
      case 'Home':
        event.preventDefault();
        this.focusFlyoutItem(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusFlyoutItem(flyoutCount - 1);
        break;
      case 'Tab':
        this.closeFlyout();
        this.closeDropdown();
        break;
    }
  };

  <template>
    {{! Desktop Navigation }}
    <nav
      class="tio-mega-menu hidden md:block sticky top-0 z-20"
      role="navigation"
      aria-label="Main navigation"
      ...attributes
    >
      <div class="{{if @isFullscreen 'px-6' 'max-w-7xl mx-auto px-6'}}">
        <div class="tio-row-extra-tight">
          {{#each this.visibleNavItems as |item|}}
            {{#if item.dropdown}}
              {{! Dropdown nav item }}
              <div class="relative">
                <button
                  type="button"
                  class="tio-mega-menu__nav-item {{if (eq this.openDropdownId item.id) 'is-open'}}"
                  aria-expanded="{{if (eq this.openDropdownId item.id) 'true' 'false'}}"
                  aria-haspopup="menu"
                  {{on "click" (fn this.toggleDropdown item.id)}}
                  {{on "keydown" (fn this.handleTriggerKeyDown item.id)}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{18}} />
                  {{/let}}
                  <span>{{item.label}}</span>
                  <PhCaretDown
                    @size={{14}}
                    class="transition-transform
                      {{if (eq this.openDropdownId item.id) 'rotate-180'}}"
                  />
                </button>

                {{#if (eq this.openDropdownId item.id)}}
                  {{! Backdrop }}
                  <button
                    type="button"
                    class="fixed inset-0 z-20"
                    aria-label="Close menu"
                    tabindex="-1"
                    {{on "click" this.closeDropdown}}
                  ></button>

                  {{! Mega-menu dropdown }}
                  <div
                    class="tio-mega-menu__panel absolute left-0 mt-0 w-80 rounded-b-lg border-t-0 z-30 animate-in fade-in slide-in-from-top-2 duration-200"
                    role="menu"
                  >
                    <div class="p-2" role="group">
                      {{#each (getVisibleDropdownItems item.dropdown) as |dropdownItem menuIndex|}}
                        {{#if dropdownItem.flyout}}
                          {{! Flyout submenu - uses relative positioning with hover on container }}
                          {{! The entire container (trigger + flyout) shares the same hover zone }}
                          <div
                            class="relative"
                            role="none"
                            {{on "mouseenter" (fn this.openFlyout dropdownItem.label)}}
                            {{on "mouseleave" this.closeFlyout}}
                          >
                            <button
                              type="button"
                              class="tio-mega-menu__item w-full group
                                {{if (eq this.openFlyoutLabel dropdownItem.label) 'is-open'}}"
                              role="menuitem"
                              tabindex={{if (eq this.focusedMenuIndex menuIndex) "0" "-1"}}
                              aria-haspopup="menu"
                              aria-expanded={{if
                                (eq this.openFlyoutLabel dropdownItem.label)
                                "true"
                                "false"
                              }}
                              {{on
                                "keydown"
                                (fn this.handleMenuKeyDown menuIndex true dropdownItem.label)
                              }}
                            >
                              <div class="tio-mega-menu__icon">
                                {{#let dropdownItem.icon as |DropdownIcon|}}
                                  <DropdownIcon @size={{20}} />
                                {{/let}}
                              </div>
                              <div class="tio-fill-min text-left">
                                <div class="tio-row-tight">
                                  <span class="tio-mega-menu__title">{{dropdownItem.label}}</span>
                                </div>
                                <span
                                  class="tio-mega-menu__desc mt-0.5 block"
                                >{{dropdownItem.description}}</span>
                              </div>
                              <PhCaretRight @size={{16}} class="tio-mega-menu__caret mt-2" />
                            </button>

                            {{! Flyout panel - positioned based on viewport space }}
                            {{#if (eq this.openFlyoutLabel dropdownItem.label)}}
                              <div
                                class="tio-mega-menu__panel absolute top-0 w-72 rounded-lg animate-in fade-in duration-150 z-30"
                                {{positionFlyout}}
                              >
                                <div class="p-2" role="menu">
                                  {{#each dropdownItem.flyout as |flyoutItem flyoutIndex|}}
                                    <LinkTo
                                      @route={{flyoutItem.route}}
                                      class="tio-mega-menu__item group"
                                      role="menuitem"
                                      tabindex={{if
                                        (eq this.focusedFlyoutIndex flyoutIndex)
                                        "0"
                                        "-1"
                                      }}
                                      {{on "click" this.handleLinkClick}}
                                      {{on
                                        "keydown"
                                        (fn this.handleFlyoutKeyDown menuIndex flyoutIndex)
                                      }}
                                    >
                                      <div class="tio-mega-menu__icon">
                                        {{#let flyoutItem.icon as |FlyoutIcon|}}
                                          <FlyoutIcon @size={{18}} />
                                        {{/let}}
                                      </div>
                                      <div class="tio-fill-min">
                                        <span
                                          class="tio-mega-menu__title"
                                        >{{flyoutItem.label}}</span>
                                        <span
                                          class="tio-mega-menu__desc mt-0.5 block"
                                        >{{flyoutItem.description}}</span>
                                      </div>
                                    </LinkTo>
                                  {{/each}}
                                </div>
                              </div>
                            {{/if}}
                          </div>
                        {{else}}
                          {{! Regular dropdown link }}
                          <LinkTo
                            @route={{dropdownItem.route}}
                            class="tio-mega-menu__item group"
                            role="menuitem"
                            tabindex={{if (eq this.focusedMenuIndex menuIndex) "0" "-1"}}
                            {{on "click" this.handleLinkClick}}
                            {{on "keydown" (fn this.handleMenuKeyDown menuIndex false "")}}
                          >
                            <div class="tio-mega-menu__icon">
                              {{#let dropdownItem.icon as |DropdownIcon|}}
                                <DropdownIcon @size={{20}} />
                              {{/let}}
                            </div>
                            <div class="tio-fill-min">
                              <div class="tio-row-tight">
                                <span class="tio-mega-menu__title">{{dropdownItem.label}}</span>
                                {{#if dropdownItem.stat}}
                                  <span
                                    class="tio-mega-menu__badge tio-text-xs-medium px-2 py-0.5 rounded-full"
                                  >{{dropdownItem.stat}}</span>
                                {{/if}}
                              </div>
                              <p class="tio-mega-menu__desc mt-0.5">{{dropdownItem.description}}</p>
                            </div>
                          </LinkTo>
                        {{/if}}
                      {{/each}}
                    </div>

                    {{! CTA at bottom }}
                    <div class="tio-mega-menu__cta p-3">
                      <LinkTo
                        @route={{getFirstDropdownRoute item}}
                        class="tio-row-center gap-2 tio-mega-menu__cta-link w-full py-2 px-4 tio-text-sm-medium rounded-lg"
                        {{on "click" this.handleLinkClick}}
                      >
                        View
                        {{item.label}}
                        Dashboard →
                      </LinkTo>
                    </div>
                  </div>
                {{/if}}
              </div>
            {{else}}
              {{! Simple nav item }}
              <LinkTo @route={{item.route}} class="tio-mega-menu__nav-item">
                {{#let item.icon as |Icon|}}
                  <Icon @size={{18}} />
                {{/let}}
                <span>{{item.label}}</span>
              </LinkTo>
            {{/if}}
          {{/each}}
        </div>
      </div>
    </nav>

    {{! Mobile Navigation }}
    <nav
      class="tio-mega-menu md:hidden sticky top-0 z-20"
      role="navigation"
      aria-label="Mobile navigation"
    >
      <div class="tio-row-between px-4 py-3">
        <div class="tio-row-tight">
          {{#if @logo}}
            {{#let @logo as |Logo|}}
              <Logo />
            {{/let}}
          {{/if}}
          <span class="tio-fg-primary font-bold">{{this.brandLabel}}</span>
        </div>

        <button
          type="button"
          class="tio-mega-menu__icon-btn"
          aria-label="{{if this.isMobileMenuOpen 'Close menu' 'Open menu'}}"
          aria-expanded="{{if this.isMobileMenuOpen 'true' 'false'}}"
          {{on "click" this.toggleMobileMenu}}
        >
          {{#if this.isMobileMenuOpen}}
            <PhX @size={{24}} />
          {{else}}
            <PhList @size={{24}} />
          {{/if}}
        </button>
      </div>

      {{#if this.isMobileMenuOpen}}
        {{! Mobile menu overlay }}
        <button
          type="button"
          class="fixed inset-0 bg-black/50 z-30"
          aria-label="Close menu"
          {{on "click" this.closeMobileMenu}}
        ></button>

        {{! Mobile menu panel }}
        <div
          class="tio-mega-menu__mobile-panel fixed top-0 right-0 bottom-0 w-80 max-w-[calc(100vw-48px)] z-40 shadow-xl overflow-y-auto animate-in slide-in-from-right duration-300"
        >
          <div class="tio-row-between tio-mega-menu__mobile-header px-4 py-3">
            <span class="tio-fg-primary font-bold">Menu</span>
            <button
              type="button"
              class="tio-mega-menu__icon-btn"
              aria-label="Close menu"
              {{on "click" this.closeMobileMenu}}
            >
              <PhX @size={{20}} />
            </button>
          </div>

          <div class="py-2">
            {{#each this.visibleNavItems as |item|}}
              {{#if item.dropdown}}
                {{! Accordion nav item }}
                <div class="tio-mega-menu__mobile-divider">
                  <button
                    type="button"
                    class="tio-row-between w-full px-4 py-3 text-left"
                    aria-expanded="{{if (eq this.mobileExpandedId item.id) 'true' 'false'}}"
                    {{on "click" (fn this.toggleMobileExpanded item.id)}}
                  >
                    <div class="tio-row">
                      {{#let item.icon as |Icon|}}
                        <Icon @size={{20}} class="tio-fg-muted" />
                      {{/let}}
                      <span class="tio-fg-primary font-medium">{{item.label}}</span>
                    </div>
                    <PhCaretDown
                      @size={{16}}
                      class="tio-mega-menu__caret transition-transform
                        {{if (eq this.mobileExpandedId item.id) 'rotate-180'}}"
                    />
                  </button>

                  {{#if (eq this.mobileExpandedId item.id)}}
                    <div class="tio-mega-menu__mobile-section pb-2">
                      {{#each (getVisibleDropdownItems item.dropdown) as |dropdownItem|}}
                        {{#if dropdownItem.flyout}}
                          {{! Nested accordion for flyout }}
                          <div>
                            <button
                              type="button"
                              class="tio-row-between tio-mega-menu__mobile-row w-full px-4 py-3 pl-12 text-left transition-colors"
                              aria-expanded="{{if
                                (eq this.mobileExpandedFlyout dropdownItem.label)
                                'true'
                                'false'
                              }}"
                              {{on "click" (fn this.toggleMobileFlyout dropdownItem.label)}}
                            >
                              <div class="tio-row">
                                {{#let dropdownItem.icon as |DropdownIcon|}}
                                  <DropdownIcon @size={{18}} class="tio-fg-disabled" />
                                {{/let}}
                                <span
                                  class="tio-fg-secondary tio-text-sm-medium"
                                >{{dropdownItem.label}}</span>
                              </div>
                              <PhCaretDown
                                @size={{14}}
                                class="tio-mega-menu__caret transition-transform
                                  {{if
                                    (eq this.mobileExpandedFlyout dropdownItem.label)
                                    'rotate-180'
                                  }}"
                              />
                            </button>

                            {{#if (eq this.mobileExpandedFlyout dropdownItem.label)}}
                              <div class="tio-mega-menu__mobile-subsection pb-1">
                                {{#each dropdownItem.flyout as |flyoutItem|}}
                                  <LinkTo
                                    @route={{flyoutItem.route}}
                                    class="tio-row tio-mega-menu__mobile-row--deep px-4 py-2.5 pl-20 transition-colors"
                                    {{on "click" this.handleLinkClick}}
                                  >
                                    {{#let flyoutItem.icon as |FlyoutIcon|}}
                                      <FlyoutIcon @size={{16}} class="tio-fg-disabled" />
                                    {{/let}}
                                    <span
                                      class="tio-mega-menu__mobile-leaf-label"
                                    >{{flyoutItem.label}}</span>
                                  </LinkTo>
                                {{/each}}
                              </div>
                            {{/if}}
                          </div>
                        {{else}}
                          {{! Regular dropdown link }}
                          <LinkTo
                            @route={{dropdownItem.route}}
                            class="tio-row tio-mega-menu__mobile-row px-4 py-3 pl-12 transition-colors"
                            {{on "click" this.handleLinkClick}}
                          >
                            {{#let dropdownItem.icon as |DropdownIcon|}}
                              <DropdownIcon @size={{18}} class="tio-fg-disabled" />
                            {{/let}}
                            <div class="flex-1">
                              <div class="tio-row-tight">
                                <span
                                  class="tio-fg-secondary tio-text-sm-medium"
                                >{{dropdownItem.label}}</span>
                                {{#if dropdownItem.stat}}
                                  <span
                                    class="tio-mega-menu__badge text-xs px-1.5 py-0.5 rounded"
                                  >{{dropdownItem.stat}}</span>
                                {{/if}}
                              </div>
                            </div>
                          </LinkTo>
                        {{/if}}
                      {{/each}}
                    </div>
                  {{/if}}
                </div>
              {{else}}
                {{! Simple mobile nav item }}
                <LinkTo
                  @route={{item.route}}
                  class="tio-row tio-mega-menu__mobile-divider tio-mega-menu__nav-item-mobile px-4 py-3 transition-colors"
                  {{on "click" this.handleLinkClick}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{20}} class="tio-fg-muted" />
                  {{/let}}
                  <span class="tio-fg-primary font-medium">{{item.label}}</span>
                </LinkTo>
              {{/if}}
            {{/each}}
          </div>
        </div>
      {{/if}}
    </nav>
  </template>
}

export default MegaMenu;
